// app/javascript/widget/composer.ts — поле ввода реплики.
//
// Композер знает про себя три вещи: как расти под набранный текст (autosize.ts
// считает высоту, здесь только чтения и записи стиля), когда показывать
// крестик очистки и что Enter отправляет, а Shift+Enter переносит строку.
//
// Отдельным модулем, потому что у поля ввода есть своя мина: сброс height в
// `auto` и следом чтение scrollHeight — это принудительный синхронный пересчёт
// вёрстки ВСЕЙ чужой страницы. На каждое нажатие клавиши он даёт «недопустимо
// большую задержку отображения символа» (жалоба гостя №128, 18.08.2026),
// поэтому замеры схлопываются в один кадр, а записи делаются только когда
// что-то меняют.
import { fitComposerHeight, needsHeightReset, needsHeightWrite, FRESH_COMPOSER } from './autosize.ts';
import type { ComposerMemo } from './autosize.ts';

export interface ComposerHost {
  /** Отправить набранное (Enter или кнопка). */
  send(): void;
  /** Потолок высоты поля в пикселях. */
  maxHeight: number;
}

export class Composer {
  private readonly input: HTMLTextAreaElement;
  private readonly clearButton: HTMLElement;
  private readonly host: ComposerHost;
  private memo: ComposerMemo = FRESH_COMPOSER;
  private frame = 0;

  constructor(input: HTMLTextAreaElement, clearButton: HTMLElement, host: ComposerHost) {
    this.input = input;
    this.clearButton = clearButton;
    this.host = host;
  }

  /** Повесить обработчики поля: Enter отправляет, ввод растит поле. */
  listen(): void {
    this.input.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' || event.shiftKey) return;

      event.preventDefault();
      this.host.send();
    });
    this.input.addEventListener('input', () => this.scheduleResize());
  }

  /** Пересчёт высоты не чаще кадра: пачка нажатий даёт один замер и не держит
   *  обработчик клавиши. */
  scheduleResize(): void {
    this.syncClearButton();
    if (this.frame) return;

    this.frame = requestAnimationFrame(() => {
      this.frame = 0;
      this.resize();
    });
  }

  /** Растит поле по содержимому до потолка, дальше — внутренний скролл.
   *  Замеры читаются ДО записей, записи делаются только когда что-то меняют. */
  resize(): void {
    const now = { length: this.input.value.length, width: this.input.clientWidth };
    const reset = needsHeightReset(this.memo, now);
    if (reset) this.input.style.height = 'auto';
    const height = fitComposerHeight(this.input.scrollHeight, this.host.maxHeight);
    if (needsHeightWrite(this.memo, height, reset)) this.input.style.height = `${height}px`;
    this.memo = { ...now, height };
    this.syncClearButton();
  }

  /** Одно нажатие стирает всё набранное; поле сбрасывается в одну строку и
   *  возвращает фокус — посетитель сразу печатает заново. */
  clear(): void {
    this.input.value = '';
    this.resize();
    this.input.focus();
  }

  /** Крестик очистки виден только при непустом поле — пустому чистить нечего. */
  private syncClearButton(): void {
    this.clearButton.classList.toggle('hidden', this.input.value.length === 0);
  }
}
